'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { User, Crown, BookMarked, Calendar, Settings, LogOut, ChevronRight, Edit3, Check, X, Shield, Bell, Sparkles, Mail, Clock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SavedArticle {
  id: string;
  article_slug: string;
  article_title: string;
  saved_at: string;
}

export default function ProfilePage() {
  const { user, profile, subscription, isSubscribed, signOut, loading } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [savedArticles, setSavedArticles] = useState<SavedArticle[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'reading' | 'settings'>('profile');

  // Edit states
  const [editingName, setEditingName] = useState(false);
  const [fullName, setFullName] = useState('');
  const [editingInterest, setEditingInterest] = useState(false);
  const [primaryInterest, setPrimaryInterest] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Notification prefs (local state)
  const [notifNewArticles, setNotifNewArticles] = useState(true);
  const [notifWeeklyDigest, setNotifWeeklyDigest] = useState(true);
  const [notifEvents, setNotifEvents] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      router.replace('/sign-up-login-screen');
    }
  }, [user, loading]);

  useEffect(() => {
    if (profile?.full_name) setFullName(profile.full_name);
    else if (user?.user_metadata?.full_name) setFullName(user.user_metadata.full_name);
    if (profile?.primary_interest) setPrimaryInterest(profile.primary_interest);
  }, [profile, user]);

  useEffect(() => {
    if (!user) return;
    const fetchSaved = async () => {
      setLoadingArticles(true);
      try {
        const { data } = await supabase
          .from('saved_articles')
          .select('*')
          .eq('user_id', user.id)
          .order('saved_at', { ascending: false })
          .limit(20);
        setSavedArticles(data || []);
      } catch {
        setSavedArticles([]);
      } finally {
        setLoadingArticles(false);
      }
    };
    fetchSaved();
  }, [user]);

  const handleSaveName = async () => {
    if (!user || !fullName.trim()) return;
    setSaving(true);
    try {
      await supabase.from('user_profiles').update({ full_name: fullName.trim() }).eq('id', user.id);
      setSavedMsg('Name updated');
      setTimeout(() => setSavedMsg(''), 2500);
      setEditingName(false);
    } catch {}
    setSaving(false);
  };

  const handleSaveInterest = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await supabase.from('user_profiles').update({ primary_interest: primaryInterest.trim() }).eq('id', user.id);
      setSavedMsg('Interest updated');
      setTimeout(() => setSavedMsg(''), 2500);
      setEditingInterest(false);
    } catch {}
    setSaving(false);
  };

  const handleSignOut = async () => {
    await signOut();
    router.push('/');
  };

  const getInitials = () => {
    const name = profile?.full_name || user?.user_metadata?.full_name || user?.email || '';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const getMemberSince = () => {
    const date = profile?.created_at || user?.created_at;
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const formatSavedDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getPlanLabel = () => {
    if (!subscription) return 'No active plan';
    return subscription.plan === 'annual' ? 'Annual Membership' : 'Monthly Membership';
  };

  const getExpiryDate = () => {
    if (!subscription?.expires_at) return '—';
    return new Date(subscription.expires_at).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AppLayout currentPath="/profile">
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-6 h-6 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!user) return null;

  const interests = [
    'Rare Timepieces', 'Extraordinary Places', 'Art & Collecting',
    'Whisky & Spirits', 'Private Clubs', 'Luxury Travel',
    'Jewellery & Gems', 'Automobiles', 'Aviation & Yachts',
    'Architecture & Design', 'Royalty & Dynasties'
  ];

  return (
    <AppLayout currentPath="/profile">
      <div className="max-w-2xl mx-auto px-4 lg:px-6 py-8">

        {/* Profile Header */}
        <div className="relative mb-8">
          {/* Background accent */}
          <div className="absolute inset-0 -mx-4 -mt-8 h-32 bg-gradient-to-b from-[var(--gold)]/5 to-transparent pointer-events-none" />

          <div className="relative flex items-end gap-5 pt-6">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[var(--gold)]/20 to-[var(--gold)]/5 border-2 border-[var(--gold)]/40 flex items-center justify-center">
                <span className="text-2xl font-bold text-[var(--gold)]">{getInitials()}</span>
              </div>
              {isSubscribed() && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-[var(--gold)] rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                  <Crown size={12} className="text-black" fill="black" />
                </div>
              )}
            </div>

            {/* Name + status */}
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="font-editorial text-2xl font-bold text-[var(--foreground)] leading-tight truncate">
                {profile?.full_name || user?.user_metadata?.full_name || 'Member'}
              </h1>
              <p className="text-sm text-[var(--slate-muted)] mt-0.5 truncate">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {isSubscribed() ? (
                  <div className="flex items-center gap-1.5 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-full px-2.5 py-1">
                    <Crown size={10} className="text-[var(--gold)]" fill="currentColor" />
                    <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[var(--gold)]">Active Member</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-stone-100 border border-stone-200 rounded-full px-2.5 py-1">
                    <User size={10} className="text-stone-500" />
                    <span className="text-[10px] font-semibold tracking-wide text-stone-500">Free Account</span>
                  </div>
                )}
                <span className="text-[11px] text-[var(--slate-muted)]">Member since {getMemberSince()}</span>
              </div>
            </div>

            <button
              onClick={handleSignOut}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs text-[var(--slate-muted)] hover:text-red-400 transition-colors pb-1"
            >
              <LogOut size={13} />
              Sign Out
            </button>
          </div>
        </div>

        {/* Saved message toast */}
        {savedMsg && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
            <Check size={14} />
            {savedMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[var(--border)] mb-6">
          {([
            { key: 'profile', label: 'Profile', icon: User },
            { key: 'reading', label: 'Reading History', icon: BookMarked },
            { key: 'settings', label: 'Settings', icon: Settings },
          ] as const).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold tracking-[0.08em] uppercase transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-[var(--gold)] text-[var(--gold)]'
                  : 'border-transparent text-[var(--slate-muted)] hover:text-[var(--foreground)]'
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="space-y-5">

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Saved Articles', value: loadingArticles ? '…' : String(savedArticles.length), icon: BookMarked },
                { label: 'Member Since', value: getMemberSince().split(' ')[0], icon: Calendar },
                { label: 'Plan', value: subscription?.plan === 'annual' ? 'Annual' : subscription?.plan === 'monthly' ? 'Monthly' : 'Free', icon: Sparkles },
              ].map((stat) => {
                const StatIcon = stat.icon;
                return (
                  <div key={stat.label} className="border border-[var(--border)] rounded-lg p-4 bg-[var(--secondary)] text-center">
                    <StatIcon size={16} className="text-[var(--gold)] mx-auto mb-2" />
                    <p className="text-base font-bold text-[var(--foreground)]">{stat.value}</p>
                    <p className="text-[10px] text-[var(--slate-muted)] mt-0.5 uppercase tracking-wide leading-tight">{stat.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Membership Card */}
            {isSubscribed() && (
              <div className="relative rounded-xl overflow-hidden border border-[var(--gold)]/30 bg-gradient-to-br from-[#0a0a0a] to-[#111]">
                <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[var(--gold)] font-bold mb-1">Membership</p>
                    <h2 className="text-lg font-bold text-white">{getPlanLabel()}</h2>
                    <p className="text-[11px] text-[#888] mt-1">Renews {getExpiryDate()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/30 rounded-full px-3 py-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      <span className="text-[11px] text-green-400 font-semibold">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Details */}
            <div className="border border-[var(--border)] rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--secondary)]">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)]">Account Details</p>
              </div>

              {/* Full Name */}
              <div className="px-4 py-4 border-b border-[var(--border)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--slate-muted)] mb-1">Full Name</p>
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="flex-1 text-sm border border-[var(--border)] rounded px-2.5 py-1.5 bg-white text-[var(--foreground)] focus:outline-none focus:border-[var(--gold)]"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                        />
                        <button onClick={handleSaveName} disabled={saving} className="p-1.5 text-green-600 hover:text-green-700">
                          <Check size={15} />
                        </button>
                        <button onClick={() => setEditingName(false)} className="p-1.5 text-[var(--slate-muted)] hover:text-red-400">
                          <X size={15} />
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-[var(--foreground)]">
                        {profile?.full_name || user?.user_metadata?.full_name || '—'}
                      </p>
                    )}
                  </div>
                  {!editingName && (
                    <button onClick={() => setEditingName(true)} className="p-1.5 text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors mt-3">
                      <Edit3 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="px-4 py-4 border-b border-[var(--border)]">
                <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--slate-muted)] mb-1">Email Address</p>
                <div className="flex items-center gap-2">
                  <Mail size={13} className="text-[var(--slate-muted)]" />
                  <p className="text-sm text-[var(--foreground)]">{user?.email}</p>
                  <div className="flex items-center gap-1 ml-auto bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    <Shield size={9} className="text-green-600" />
                    <span className="text-[9px] text-green-600 font-semibold">Verified</span>
                  </div>
                </div>
              </div>

              {/* Primary Interest */}
              <div className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--slate-muted)] mb-1">Primary Interest</p>
                    {editingInterest ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {interests.map((interest) => (
                            <button
                              key={interest}
                              onClick={() => setPrimaryInterest(interest)}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                primaryInterest === interest
                                  ? 'bg-[var(--gold)] border-[var(--gold)] text-white'
                                  : 'border-[var(--border)] text-[var(--slate-muted)] hover:border-[var(--gold)]/50'
                              }`}
                            >
                              {interest}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={handleSaveInterest} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[var(--gold)] text-white text-xs font-semibold rounded">
                            <Check size={12} /> Save
                          </button>
                          <button onClick={() => setEditingInterest(false)} className="px-3 py-1.5 border border-[var(--border)] text-[var(--slate-muted)] text-xs rounded">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--foreground)]">
                        {profile?.primary_interest || user?.user_metadata?.primary_interest || '—'}
                      </p>
                    )}
                  </div>
                  {!editingInterest && (
                    <button onClick={() => setEditingInterest(true)} className="p-1.5 text-[var(--slate-muted)] hover:text-[var(--gold)] transition-colors mt-3">
                      <Edit3 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="border border-[var(--border)] rounded-lg overflow-hidden">
              {[
                { label: 'My Library', sub: 'Saved articles & collections', href: '/my-library', icon: BookMarked },
                { label: 'Membership & Billing', sub: 'Manage plan, billing cycle & payment', href: '/subscription', icon: Crown },
                { label: 'My Dashboard', sub: 'Overview & activity', href: '/subscriber-dashboard', icon: Sparkles },
              ].map((item) => {
                const ItemIcon = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex items-center gap-3 px-4 py-3.5 border-b border-[var(--border)] last:border-0 hover:bg-[var(--secondary)] transition-colors group"
                  >
                    <ItemIcon size={16} className="text-[var(--gold)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--foreground)]">{item.label}</p>
                      <p className="text-[11px] text-[var(--slate-muted)]">{item.sub}</p>
                    </div>
                    <ChevronRight size={14} className="text-[var(--slate-muted)] group-hover:text-[var(--gold)] transition-colors" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* READING HISTORY TAB */}
        {activeTab === 'reading' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)]">
                {loadingArticles ? 'Loading…' : `${savedArticles.length} Saved Articles`}
              </p>
              {savedArticles.length > 0 && (
                <Link href="/my-library" className="text-[11px] text-[var(--gold)] hover:underline font-semibold">
                  View All in Library →
                </Link>
              )}
            </div>

            {loadingArticles ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : savedArticles.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-[var(--border)] rounded-lg">
                <BookMarked size={28} className="text-[var(--slate-muted)] mx-auto mb-3" />
                <p className="text-sm font-medium text-[var(--foreground)] mb-1">No saved articles yet</p>
                <p className="text-[12px] text-[var(--slate-muted)] mb-4">Bookmark articles as you read to find them here</p>
                <Link href="/editorial-discovery-feed" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--gold)] text-white text-xs font-bold tracking-wide uppercase rounded hover:bg-[var(--gold-dark)] transition-colors">
                  Explore Discoveries
                </Link>
              </div>
            ) : (
              savedArticles.map((article) => (
                <Link
                  key={article.id}
                  href={`/article?slug=${article.article_slug}`}
                  className="flex items-start gap-3 p-3.5 border border-[var(--border)] rounded-lg hover:border-[var(--gold)]/30 hover:bg-[var(--secondary)] transition-all group"
                >
                  <div className="w-8 h-8 rounded bg-[var(--gold)]/10 border border-[var(--gold)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <BookMarked size={13} className="text-[var(--gold)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--foreground)] line-clamp-2 group-hover:text-[var(--gold)] transition-colors">
                      {article.article_title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Clock size={10} className="text-[var(--slate-muted)]" />
                      <span className="text-[10px] text-[var(--slate-muted)]">Saved {formatSavedDate(article.saved_at)}</span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[var(--slate-muted)] group-hover:text-[var(--gold)] transition-colors flex-shrink-0 mt-1" />
                </Link>
              ))
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div className="space-y-5">

            {/* Notifications */}
            <div className="border border-[var(--border)] rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--secondary)]">
                <div className="flex items-center gap-2">
                  <Bell size={13} className="text-[var(--gold)]" />
                  <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)]">Notification Preferences</p>
                </div>
              </div>
              {[
                { label: 'New Articles & Discoveries', sub: 'When new content is published', state: notifNewArticles, setter: setNotifNewArticles },
                { label: 'Weekly Private Edition', sub: 'Curated weekly digest every Friday', state: notifWeeklyDigest, setter: setNotifWeeklyDigest },
                { label: 'Exclusive Events', sub: 'Private events and experiences', state: notifEvents, setter: setNotifEvents },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between px-4 py-3.5 border-b border-[var(--border)] last:border-0">
                  <div>
                    <p className="text-sm font-medium text-[var(--foreground)]">{item.label}</p>
                    <p className="text-[11px] text-[var(--slate-muted)] mt-0.5">{item.sub}</p>
                  </div>
                  <button
                    onClick={() => item.setter(!item.state)}
                    className={`relative w-10 h-5.5 rounded-full transition-colors flex-shrink-0 ${item.state ? 'bg-[var(--gold)]' : 'bg-stone-200'}`}
                    style={{ height: '22px', width: '40px' }}
                  >
                    <span
                      className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${item.state ? 'translate-x-[19px]' : 'translate-x-0.5'}`}
                      style={{ width: '18px', height: '18px', transform: item.state ? 'translateX(19px)' : 'translateX(2px)' }}
                    />
                  </button>
                </div>
              ))}
            </div>

            {/* Account Actions */}
            <div className="border border-[var(--border)] rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--secondary)]">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)]">Account</p>
              </div>
              <div className="px-4 py-3.5 border-b border-[var(--border)]">
                <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--slate-muted)] mb-1">Password</p>
                <p className="text-sm text-[var(--foreground)]">••••••••••••</p>
                <p className="text-[11px] text-[var(--slate-muted)] mt-0.5">Use the sign-in page to reset your password via email</p>
              </div>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-4 py-3.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <LogOut size={14} />
                Sign Out of Account
              </button>
            </div>

            {/* App Info */}
            <div className="border border-[var(--border)] rounded-lg px-4 py-4 bg-[var(--secondary)]">
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[var(--slate-muted)] mb-2">About</p>
              <p className="text-xs text-[var(--slate-muted)]">The Private World of Luxury</p>
              <p className="text-[11px] text-[var(--slate-muted)] mt-0.5">A private world of extraordinary things</p>
              <p className="text-[10px] text-[var(--slate-muted)] mt-2 opacity-60">Version 1.0 · theprivateworld.app</p>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
